import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOffer, BookOffersRequest } from 'xrpl';
import { useXRPLContext } from '@/src/contexts';

interface OrderbookState {
  offers: BookOffer[];
  isLoading: boolean;
  isError: boolean;
  error?: Error;
}

interface UseOrderbookSubscriptionOptions {
  enabled?: boolean;
  limit?: number; // Max number of offers to fetch (default: 50, XRPL max: ~400)
}

/**
 * Hook that subscribes to real-time orderbook updates using XRPL WebSocket
 *
 * Flow:
 * 1. Fetch initial snapshot with snapshot: true
 * 2. Subscribe to orderbook changes
 * 3. Listen to transaction stream for incremental updates
 * 4. Merge updates into existing orderbook state
 */
export const useOrderbookSubscription = (
  request: BookOffersRequest | null,
  options: UseOrderbookSubscriptionOptions = {}
) => {
  const { client, isConnected } = useXRPLContext();
  const { enabled = true, limit = 50 } = options;

  const [state, setState] = useState<OrderbookState>({
    offers: [],
    isLoading: true,
    isError: false,
  });

  // Create a stable key for the request to avoid infinite re-renders
  const requestKey = useMemo(() => {
    if (!request) return null;
    return JSON.stringify({
      taker_gets: request.taker_gets,
      taker_pays: request.taker_pays,
    });
  }, [request]);

  // Store the latest request in a ref to avoid stale closures
  const requestRef = useRef(request);
  requestRef.current = request;

  // Track last seen ledger index for desync detection
  const lastLedgerIndexRef = useRef<number | null>(null);

  // Setup subscription when connected and request changes
  useEffect(() => {
    if (!isConnected || !request || !enabled || !requestKey) {
      return;
    }

    let isSubscribed = true;

    // Helper function to wait for client to be fully connected
    const waitForConnection = async (maxRetries = 10, delayMs = 500): Promise<boolean> => {
      for (let i = 0; i < maxRetries; i++) {
        if (client.isConnected()) {
          return true;
        }
        console.debug(`[orderbook] Waiting for connection... (${i + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
      return false;
    };

    // Fetch initial orderbook snapshot
    const fetchSnapshot = async (showLoading: boolean = true) => {
      if (!requestRef.current) return;

      try {
        if (showLoading) {
          setState((prev) => ({ ...prev, isLoading: true, isError: false }));
        }

        // Wait for connection to be ready
        const isReady = await waitForConnection();
        if (!isReady || !isSubscribed) {
          console.error('[orderbook] Connection not ready after waiting');
          if (isSubscribed) {
            setState({
              offers: [],
              isLoading: false,
              isError: true,
              error: new Error('WebSocket connection not ready'),
            });
          }
          return;
        }

        const response = await client.request({
          ...requestRef.current,
          command: 'book_offers',
          limit,
        });

        if (isSubscribed) {
          setState({
            offers: response.result.offers || [],
            isLoading: false,
            isError: false,
          });

          // Reset ledger tracking after fresh snapshot
          if (response.result.ledger_current_index || response.result.ledger_index) {
            lastLedgerIndexRef.current = response.result.ledger_current_index || response.result.ledger_index || null;
            console.debug('[orderbook] Ledger tracking initialized:', lastLedgerIndexRef.current);
          }
        }
      } catch (error) {
        console.error('[orderbook] Failed to fetch snapshot:', error);
        if (isSubscribed) {
          setState({
            offers: [],
            isLoading: false,
            isError: true,
            error: error instanceof Error ? error : new Error('Failed to fetch orderbook'),
          });
        }
      }
    };

    // Subscribe to specific orderbook using books parameter
    const subscribeToOrderbook = async () => {
      if (!requestRef.current) return;

      try {
        // Wait for connection to be ready
        const isReady = await waitForConnection();
        if (!isReady || !isSubscribed) {
          console.error('[orderbook] Connection not ready for subscription');
          return;
        }

        // Subscribe to this specific order book
        await client.request({
          command: 'subscribe',
          books: [
            {
              taker_gets: requestRef.current.taker_gets,
              taker_pays: requestRef.current.taker_pays,
              both: true, // Subscribe to both sides of the book
              taker: requestRef.current.taker_gets.issuer || '',
            },
          ],
        } as any);

        console.log('[orderbook] ✅ Subscribed to order book:', {
          taker_gets: requestRef.current.taker_gets,
          taker_pays: requestRef.current.taker_pays,
        });
      } catch (error) {
        console.error('[orderbook] Failed to subscribe:', error);
        if (isSubscribed) {
          setState((prev) => ({
            ...prev,
            isError: true,
            error: error instanceof Error ? error : new Error('Failed to subscribe to orderbook'),
          }));
        }
      }
    };

    // Handle transaction updates
    const handleTransaction = (tx: any) => {
      // Transaction data can be in tx_json or transaction field
      const transaction = tx.tx_json || tx.transaction;

      // Desync detection: Check for ledger sequence gaps
      const currentLedgerIndex = tx.ledger_index;
      if (currentLedgerIndex && lastLedgerIndexRef.current !== null) {
        const gap = currentLedgerIndex - lastLedgerIndexRef.current;
        if (gap > 1) {
          console.warn(
            `[orderbook] ⚠️ Desync detected! Ledger gap: ${lastLedgerIndexRef.current} -> ${currentLedgerIndex} (${gap - 1} ledgers missed)`
          );
          // Resync by fetching fresh snapshot
          fetchSnapshot(false);
          lastLedgerIndexRef.current = currentLedgerIndex;
          return; // Skip processing this transaction, snapshot will have latest state
        }
      }
      if (currentLedgerIndex) {
        lastLedgerIndexRef.current = currentLedgerIndex;
      }

      // Log offer transactions for debugging
      if (transaction?.TransactionType === 'OfferCreate' || transaction?.TransactionType === 'OfferCancel') {
        console.log('[orderbook] 📊 Offer transaction detected:', transaction.TransactionType, tx.hash);
      }

      if (!requestRef.current) {
        return;
      }

      // Only process OfferCreate and OfferCancel transactions
      if (transaction?.TransactionType !== 'OfferCreate' && transaction?.TransactionType !== 'OfferCancel') {
        return;
      }

      // For OfferCreate/OfferCancel, check if TakerGets or TakerPays matches our pair
      const txTakerGets = transaction.TakerGets;
      const txTakerPays = transaction.TakerPays;

      if (!txTakerGets || !txTakerPays) {
        return;
      }

      // Check if this affects our orderbook by comparing currencies
      const reqTakerGets = requestRef.current.taker_gets;
      const reqTakerPays = requestRef.current.taker_pays;

      console.log('[orderbook] 🔍 Checking offer match:', {
        offerType: transaction.TransactionType,
        txGets: txTakerGets,
        txPays: txTakerPays,
        reqGets: reqTakerGets,
        reqPays: reqTakerPays,
      });

      // Helper to compare currency objects
      const currencyMatches = (a: any, b: any) => {
        // Check if both are XRP (either as string or as {currency: "XRP"})
        const aIsXRP = typeof a === 'string' || a.currency === 'XRP';
        const bIsXRP = typeof b === 'string' || b.currency === 'XRP';

        if (aIsXRP && bIsXRP) return true; // Both are XRP
        if (aIsXRP || bIsXRP) return false; // Only one is XRP

        // Both are issued currencies - compare currency code and issuer
        return a.currency === b.currency && a.issuer === b.issuer;
      };

      const affectsOrderbook =
        (currencyMatches(txTakerGets, reqTakerGets) && currencyMatches(txTakerPays, reqTakerPays)) ||
        (currencyMatches(txTakerGets, reqTakerPays) && currencyMatches(txTakerPays, reqTakerGets));

      if (!affectsOrderbook) {
        console.debug('[orderbook] Transaction does not affect this orderbook');
        return;
      }

      console.log('[orderbook] ⚡ Transaction affecting orderbook!', transaction.TransactionType, tx.hash);

      // Refetch the orderbook to get the latest state (without showing loading spinner)
      fetchSnapshot(false);
    };

    // Initialize
    fetchSnapshot();
    subscribeToOrderbook();
    client.on('transaction', handleTransaction);

    // Cleanup
    return () => {
      isSubscribed = false;
      client.off('transaction', handleTransaction);
      lastLedgerIndexRef.current = null; // Reset ledger tracking

      // Unsubscribe from this specific order book
      if (requestRef.current) {
        client.request({
          command: 'unsubscribe',
          books: [
            {
              taker_gets: requestRef.current.taker_gets,
              taker_pays: requestRef.current.taker_pays,
              both: true,
            },
          ],
        } as any).catch((error) => {
          console.error('[orderbook] Failed to unsubscribe:', error);
        });

        console.log('[orderbook] 🔌 Unsubscribed from order book');
      }
    };
  }, [isConnected, requestKey, enabled, client]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    if (!requestRef.current) return;

    setState((prev) => ({ ...prev, isLoading: true, isError: false }));

    client.request({
      ...requestRef.current,
      command: 'book_offers',
      limit,
    })
      .then((response) => {
        setState({
          offers: response.result.offers || [],
          isLoading: false,
          isError: false,
        });
      })
      .catch((error) => {
        console.error('[orderbook] Failed to refetch:', error);
        setState({
          offers: [],
          isLoading: false,
          isError: true,
          error: error instanceof Error ? error : new Error('Failed to fetch orderbook'),
        });
      });
  }, [client, limit]);

  return {
    data: state.offers.length > 0 ? { offers: state.offers } : undefined,
    isLoading: state.isLoading,
    isError: state.isError,
    error: state.error,
    refetch,
    isFetching: false, // Not used in subscription mode, but kept for compatibility
  };
};
